import * as Sentry from '@sentry/react'

/**
 * Application logging for Sentry Logs.
 *
 * Deliberately NOT wired to a console integration: console output here is full of
 * third-party chatter (Clerk, React, Vite, browser deprecations) and shipping it
 * would recreate the noise problem we just cleaned up. Only explicit calls to this
 * module reach Sentry, so everything in the Logs view is our own application.
 *
 * Errors are a separate stream — those still arrive as Issues via captureException
 * and the ErrorBoundary, whether or not anything logs here.
 */
type Attributes = Record<string, string | number | boolean | undefined>

export const log = {
  debug: (message: string, attributes?: Attributes) => Sentry.logger.debug(message, attributes),
  info: (message: string, attributes?: Attributes) => Sentry.logger.info(message, attributes),
  warn: (message: string, attributes?: Attributes) => Sentry.logger.warn(message, attributes),
  error: (message: string, attributes?: Attributes) => Sentry.logger.error(message, attributes),
}

/**
 * Reads a 0..1 sample rate from an env var, falling back when unset or malformed.
 * Number('') is 0, so a blank var would otherwise silently disable sampling.
 */
export function sampleRate(raw: string | undefined, fallback: number): number {
  const parsed = Number(raw)
  return raw !== undefined && raw !== '' && Number.isFinite(parsed) && parsed >= 0 && parsed <= 1
    ? parsed
    : fallback
}
